import { WwsIndividu } from '../services/wws.service';
import { NotifiedPersonContact } from './visit.types';

/**
 * Split a WWS display name ("Jean Dupont") into first / last when the API
 * only returns a single `name` field (common on /wws/individus/search).
 */
export function splitDisplayName(name: string): { firstName?: string; lastName?: string } {
  const trimmed = name.trim();
  if (!trimmed) {
    return {};
  }
  const space = trimmed.indexOf(' ');
  if (space === -1) {
    return { firstName: trimmed };
  }
  return {
    firstName: trimmed.slice(0, space).trim(),
    lastName: trimmed.slice(space + 1).trim(),
  };
}

/**
 * Keep a WWS field only when it really is a string: the collaborator data
 * structure varies by WWS tenant (e.g. a numeric id in `email`), and a
 * non-string would crash any later `.trim()`.
 */
function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** Map a WWS individu search hit to the host contact shape used for notifications. */
export function wwsIndividuToNotifiedContact(
  host: WwsIndividu | undefined,
): NotifiedPersonContact | undefined {
  if (!host) {
    return undefined;
  }

  const record = host as Record<string, unknown>;
  let firstName =
    asString(host.firstName) ?? asString(record['first_name']) ?? asString(record['firstname']);
  let lastName =
    asString(host.lastName) ?? asString(record['last_name']) ?? asString(record['lastname']);
  // Some WWS tenants put the individu id in `email` and the address in `mail`.
  const email = [host.email, host.mail]
    .map(asString)
    .find((value) => value?.includes('@'));
  const businessPhones = Array.isArray(host.businessPhones) ? host.businessPhones : [];
  const phone = asString(businessPhones[0]) ?? asString(host.mobilePhone);

  if (!firstName?.trim() && !lastName?.trim()) {
    const fromName = splitDisplayName(
      String(host.name ?? host.displayName ?? ''),
    );
    firstName = fromName.firstName;
    lastName = fromName.lastName;
  }

  if (!firstName?.trim() && !lastName?.trim() && !email?.trim() && !phone?.trim()) {
    return undefined;
  }

  return { firstName, lastName, email, phone };
}
